import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { Banner } from '@app/components/core/banner/Banner'
import { Text } from '@app/components/core/typography/Text'
import { Button } from '@app/components/core/button/Button'
import { Form } from '@app/components/core/form/Form'
import { TextField } from '@app/components/core/form/fields/TextField'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useState } from 'react'
import { match } from 'ts-pattern'
import { EditButton } from '@app/components/core/button/EditButton'
import { z } from 'zod'

const mainInfoSchema = (errorMessage: string) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(4, errorMessage)
      .refine((value) => value.includes(' '), errorMessage),
  })

type MainInfoFormValues = {
  name: string
}

export type MainInfoEditPanelProps = {
  name: string
  email: string
  banner: string
  avatar: string
  onSave: (
    name?: string,
    email?: string,
    avatar?: File,
    banner?: File,
  ) => Promise<boolean>
}

export const MainInfoEditPanel = ({
  name,
  email,
  avatar,
  banner,
  onSave,
}: MainInfoEditPanelProps) => {
  const translations = useTranslations().app.common
  const profileTranslations = useTranslations().app.dashboard.profile
  const [editMode, setEditMode] = useState(false)

  const [newAvatar, setNewAvatar] = useState<{ preview: string; file: File }>()
  const [newBanner, setNewBanner] = useState<{ preview: string; file: File }>()

  const reset = useCallback(() => {
    if (newAvatar?.preview) {
      URL.revokeObjectURL(newAvatar.preview)
    }
    if (newBanner?.preview) {
      URL.revokeObjectURL(newBanner.preview)
    }
    setNewAvatar(null)
    setNewBanner(null)
    setEditMode(false)
  }, [newAvatar, newBanner])

  const save = useCallback(
    async ({ name }: MainInfoFormValues) => {
      const success = await onSave(name, null, newAvatar?.file, newBanner?.file)
      if (success) {
        reset()
      }
    },
    [newAvatar, newBanner, onSave, reset],
  )

  const validationMessage = profileTranslations.nameErrorMessage
  const schema = mainInfoSchema(validationMessage)

  return (
    <Column
      alignItems="stretch"
      style={{
        backgroundColor: 'var(--palette-white)',
        borderRadius: '1.25rem',
        overflow: 'clip',
      }}
    >
      <Banner
        image={newBanner ? newBanner.preview : banner}
        alt={`${name} ${translations.banner}`}
        editMode={editMode}
        onNewBannerSeleted={(file: File) =>
          setNewBanner({ preview: URL.createObjectURL(file), file })
        }
        bannerStyle={{ borderRadius: 0, height: '11rem' }}
      >
        <Avatar
          size={'large'}
          variant="pink"
          labelSource={name}
          editMode={editMode}
          image={newAvatar ? newAvatar.preview : avatar}
          onNewAvatarSeleted={(file: File) =>
            setNewAvatar({ preview: URL.createObjectURL(file), file })
          }
        />
      </Banner>
      <Column
        alignItems="stretch"
        gap={'0.5rem'}
        style={{
          padding: '0 1.5rem 1.5rem 1.5rem',
        }}
      >
        {match(editMode)
          .with(true, () => (
            <Form<MainInfoFormValues>
              key={name}
              schema={schema}
              defaultValues={{ name }}
              mode="onChange"
              onSubmit={save}
            >
              {({ formState }) => (
                <>
                  <Row
                    style={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: '0.5rem',
                    }}
                  >
                    <TextField<MainInfoFormValues>
                      name="name"
                      label={profileTranslations.fullName}
                      required={true}
                      style={{
                        flex: '1 1 auto',
                      }}
                    />
                  </Row>
                  <Row
                    style={{ justifyContent: 'flex-end', paddingTop: '1.5rem' }}
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      label={translations.cancel}
                      onClick={reset}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      label={translations.save}
                      disabled={!formState.isValid || formState.isSubmitting}
                    />
                  </Row>
                </>
              )}
            </Form>
          ))
          .otherwise(() => (
            <>
              <Row
                style={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '0.5rem',
                }}
              >
                <Title.H4 style={{ marginBottom: '0' }}>{name}</Title.H4>
                <EditButton
                  isVisible={!editMode}
                  onClick={() => setEditMode(true)}
                />
              </Row>
              <Text.Medium color="var(--gray-300)">{email}</Text.Medium>
            </>
          ))}
      </Column>
    </Column>
  )
}
